import React, { useState, useEffect } from 'react';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { authService } from '../../services/authService';

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white';

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}

const EMPTY = {
  nombre: '', nif: '', direccion: '', cp: '', localidad: '',
  telefono: '', email: '', iban: '',
};

export default function ConfigEmpresaView() {
  const [form, setForm]     = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  useEffect(() => {
    getDoc(doc(db, 'config', 'empresa'))
      .then(snap => { if (snap.exists()) setForm({ ...EMPTY, ...snap.data() }); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const resp = await fetch('/.netlify/functions/saveConfigEmpresa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify(form),
      });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error || 'Error desconocido');
      setMsg('Datos guardados correctamente');
    } catch (e) {
      setMsg('Error: ' + e.message);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 4000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-6 space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Configuración de empresa</h1>
        <p className="text-sm text-gray-500 mt-1">
          Estos datos aparecen en todas las facturas y documentos generados.
        </p>
      </div>

      {msg && (
        <p className={`text-sm font-semibold px-4 py-2.5 rounded-xl ${
          msg.startsWith('Error') ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'
        }`}>{msg}</p>
      )}

      <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4 shadow-sm">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Datos fiscales</p>

        <Field label="Nombre / Razón social">
          <input value={form.nombre} onChange={e => set('nombre', e.target.value)}
            placeholder="Ej: Climatización Zapata"
            className={inputCls} />
        </Field>

        <Field label="NIF / CIF">
          <input value={form.nif} onChange={e => set('nif', e.target.value)}
            placeholder="Ej: 12345678Z"
            className={inputCls} />
        </Field>

        <Field label="Dirección">
          <input value={form.direccion} onChange={e => set('direccion', e.target.value)}
            placeholder="Calle, número, piso…"
            className={inputCls} />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="CP">
            <input value={form.cp} onChange={e => set('cp', e.target.value)}
              placeholder="03001"
              className={inputCls} />
          </Field>
          <div className="col-span-2">
            <Field label="Localidad">
              <input value={form.localidad} onChange={e => set('localidad', e.target.value)}
                placeholder="Alicante"
                className={inputCls} />
            </Field>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4 shadow-sm">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Contacto y cobro</p>

        <Field label="Teléfono">
          <input value={form.telefono} onChange={e => set('telefono', e.target.value)}
            placeholder="Ej: 965 000 000"
            className={inputCls} />
        </Field>

        <Field label="Email de facturación">
          <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
            placeholder="facturacion@empresa.com"
            className={inputCls} />
        </Field>

        <Field label="IBAN (domiciliación)">
          <input value={form.iban} onChange={e => set('iban', e.target.value)}
            placeholder="ES00 0000 0000 0000 0000 0000"
            className={inputCls} />
        </Field>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
      >
        {saving ? 'Guardando…' : 'Guardar cambios'}
      </button>
    </div>
  );
}
